// Shared calendar styles for FullCalendar components
// Used by both TimeAndDateStep and SpaceCalendarPage

export const sharedCalendarStyles = {
  '.fc': {
    borderRadius: 2,
    overflow: 'hidden',
    fontFamily: 'inherit',
    color: '#1f1f1f !important',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)',
    background: '#ffffff !important',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    position: 'relative',
  },
  // Calendar toolbar styling
  '& .fc-toolbar': {
    backgroundColor: '#ffffff !important',
    marginBottom: '0.5rem',
    padding: '4px 8px !important',
    position: 'relative',
    zIndex: 2,
    minHeight: '36px !important',
  },
  '& .fc-toolbar-chunk': {
    display: 'flex !important',
    alignItems: 'center !important',
    gap: '12px !important',
  },
  '& .fc-button': {
    backgroundColor: '#f8f9fa !important',
    border: '1px solid #e9ecef !important',
    color: '#495057 !important',
    borderRadius: '6px !important',
    padding: '4px 12px !important',
    fontSize: '0.7rem !important',
    fontWeight: '500 !important',
    textTransform: 'none !important',
    boxShadow: 'none !important',
    transition: 'all 0.2s ease !important',
    minHeight: '28px !important',
    '&:hover': {
      backgroundColor: '#f0f2ff !important',
      borderColor: '#4E67EB !important',
      color: '#4E67EB !important',
    },
    '&:active': {
      backgroundColor: '#e8ebff !important',
      borderColor: '#3d56d9 !important',
      color: '#3d56d9 !important',
    },
    '&.fc-button-active': {
      backgroundColor: '#4E67EB !important',
      borderColor: '#4E67EB !important',
      color: '#ffffff !important',
    },
  },
  '& .fc-toolbar-title': {
    fontSize: '0.65rem !important',
    fontWeight: '600 !important',
    color: '#212529 !important',
    lineHeight: '1.1 !important',
    whiteSpace: 'nowrap !important',
    overflow: 'hidden !important',
    textOverflow: 'ellipsis !important',
    maxWidth: '200px !important',
    '@media (max-width: 768px)': {
      fontSize: '0.8rem !important',
      maxWidth: '180px !important',
      lineHeight: '1.1 !important',
    },
    '@media (max-width: 480px)': {
      fontSize: '0.75rem !important',
      maxWidth: '160px !important',
      lineHeight: '1.0 !important',
    },
  },
  // Force white backgrounds for all calendar elements
  '& .fc-daygrid-day, & .fc-daygrid-day-frame, & .fc-view-harness, & .fc-scrollgrid, & .fc-timegrid, & .fc-daygrid, & .fc-timegrid-body, & .fc-timegrid-slot, & .fc-timegrid-slot-minor, & .fc-timegrid-slot-label, & .fc-daygrid-day-bg, & .fc-daygrid-day-events, & .fc-daygrid-day-top, & .fc-scrollgrid-section, & .fc-scrollgrid-section-header, & .fc-scrollgrid-section-body, & .fc-scrollgrid-section-footer, & .fc-scrollgrid-sync-table td, & .fc-scrollgrid-sync-table th': {
    backgroundColor: '#ffffff !important',
  },
  // Make column borders gray
  '& .fc-scrollgrid-sync-table td, & .fc-scrollgrid-sync-table th, & .fc-timegrid-slot, & .fc-timegrid-slot-minor': {
    borderColor: '#e0e0e0 !important',
  },
  // Ensure day-separating column borders are visible
  '& .fc-scrollgrid-sync-table td': {
    borderRight: '1px solid #e0e0e0 !important',
  },
  '& .fc-scrollgrid-sync-table th': {
    borderRight: '1px solid #e0e0e0 !important',
  },
  // More specific targeting for FullCalendar column borders
  '& .fc-timegrid-col': {
    borderRight: '1px solid #d0d0d0 !important',
  },
  '& .fc-timegrid-slot': {
    borderRight: '1px solid #d0d0d0 !important',
  },
  '& .fc-col-header-cell': {
    borderRight: '1px solid #d0d0d0 !important',
  },
  // Ensure toolbar title is visible in light mode
  '& .fc-toolbar-title, & .fc-toolbar-title *': {
    color: '#212529 !important',
  },
  // Navigation buttons (prev, next, today) - standard
  '& .fc-prev-button, & .fc-next-button, & .fc-today-button': {
    borderRadius: '8px !important',
    padding: '6px 14px !important',
    fontSize: '0.75rem !important',
    fontWeight: '500 !important',
    minWidth: '40px !important',
    height: '36px !important',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
  },
  // View buttons (week, day) - standard
  '& .fc-timeGridWeek-button, & .fc-timeGridDay-button': {
    borderRadius: '8px !important',
    padding: '6px 16px !important',
    fontSize: '0.75rem !important',
    fontWeight: '500 !important',
    marginLeft: '8px !important',
    minWidth: 'auto !important',
    whiteSpace: 'nowrap !important',
  },
  // Month/Year title button styling
  '& .fc-monthYearTitle-button': {
    backgroundColor: 'transparent !important',
    border: 'none !important',
    color: '#212529 !important',
    fontSize: '1.1rem !important',
    fontWeight: '600 !important',
    padding: '8px 16px !important',
    marginLeft: '8px !important',
    cursor: 'default !important',
    boxShadow: 'none !important',
    transition: 'color 0.2s ease !important',
    '&:hover': {
      backgroundColor: 'transparent !important',
      color: '#495057 !important',
    },
    '&:active': {
      color: '#6c757d !important',
    },
  },
  // Mobile responsive adjustments
  '@media (max-width: 768px)': {
    '& .fc-toolbar': {
      padding: '0 4px !important',
      marginBottom: '1.5rem !important',
      flexDirection: 'row !important',
      gap: '4px !important',
      flexWrap: 'wrap !important',
      justifyContent: 'center !important',
    },
    '& .fc-toolbar-chunk': {
      flexWrap: 'nowrap !important',
      gap: '4px !important',
      justifyContent: 'center !important',
      display: 'flex !important',
      alignItems: 'center !important',
    },
    '& .fc-toolbar-chunk:first-child': {
      order: 1,
      flex: '0 0 auto !important',
    },
    '& .fc-toolbar-chunk:nth-child(2)': {
      order: 2,
      flex: '1 1 auto !important',
      textAlign: 'center !important',
      minWidth: '0 !important',
    },
    '& .fc-toolbar-chunk:last-child': {
      order: 3,
      flex: '0 0 auto !important',
    },
    '& .fc-timeGridWeek-button, & .fc-timeGridDay-button': {
      padding: '5px 12px !important',
      fontSize: '0.85rem !important',
      marginLeft: '4px !important',
      borderRadius: '6px !important',
    },
    '& .fc-prev-button, & .fc-next-button, & .fc-today-button': {
      padding: '5px 10px !important',
      fontSize: '0.85rem !important',
      minWidth: '35px !important',
      height: '32px !important',
      borderRadius: '6px !important',
    },
    // Mobile styling for monthYearTitle button
    '& .fc-monthYearTitle-button': {
      fontSize: '0.75rem !important',
      padding: '3px 4px !important',
      marginLeft: '0 !important',
      maxWidth: '100% !important',
      width: 'auto !important',
      overflow: 'hidden !important',
      textOverflow: 'ellipsis !important',
      whiteSpace: 'nowrap !important',
      textAlign: 'center !important',
      lineHeight: '1.0 !important',
    },
    // Mobile event styling
    '& .fc-event': {
      fontSize: '11px !important',
      minHeight: '24px !important',
      padding: '3px 6px !important',
    },
    // Mobile day headers
    '& .fc-col-header-cell-cushion': {
      fontSize: '13px !important',
      padding: '8px 4px !important',
      backgroundColor: '#f0f2ff !important',
      color: '#4E67EB !important',
    },
  },
  // Extra small mobile screens
  '@media (max-width: 480px)': {
    '& .fc-toolbar': {
      padding: '0 2px !important',
      marginBottom: '1rem !important',
      flexDirection: 'row !important',
      gap: '2px !important',
      flexWrap: 'wrap !important',
      justifyContent: 'center !important',
    },
    '& .fc-toolbar-chunk': {
      justifyContent: 'center !important',
      gap: '2px !important',
      display: 'flex !important',
      alignItems: 'center !important',
      flexWrap: 'nowrap !important',
    },
    '& .fc-toolbar-chunk:nth-child(2)': {
      flex: '1 1 auto !important',
      textAlign: 'center !important',
      minWidth: '0 !important',
    },
    '& .fc-monthYearTitle-button': {
      fontSize: '0.7rem !important',
      padding: '2px 3px !important',
      marginLeft: '0 !important',
      maxWidth: '100% !important',
      width: 'auto !important',
      textAlign: 'center !important',
      lineHeight: '1.0 !important',
      whiteSpace: 'nowrap !important',
      overflow: 'hidden !important',
      textOverflow: 'ellipsis !important',
    },
    '& .fc-prev-button, & .fc-next-button, & .fc-today-button': {
      padding: '5px 10px !important',
      fontSize: '0.8rem !important',
      minWidth: '32px !important',
      height: '30px !important',
      borderRadius: '6px !important',
    },
    '& .fc-timeGridWeek-button, & .fc-timeGridDay-button': {
      padding: '4px 10px !important',
      fontSize: '0.8rem !important',
      marginLeft: '3px !important',
      borderRadius: '6px !important',
    },
    // Extra small mobile event styling
    '& .fc-event': {
      fontSize: '10px !important',
      minHeight: '22px !important',
      padding: '2px 4px !important',
    },
    // Extra small mobile day headers
    '& .fc-col-header-cell-cushion': {
      fontSize: '12px !important',
      padding: '6px 2px !important',
      backgroundColor: '#f0f2ff !important',
      color: '#4E67EB !important',
    },
  },
  // Event styling
  '& .fc-event': {
    fontSize: '12px',
    whiteSpace: 'nowrap !important',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '1.4',
    minHeight: '24px',
    padding: '3px 6px',
    cursor: 'pointer',
    borderRadius: '4px !important',
    border: '1px solid rgba(0,0,0,0.1) !important',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1) !important',
    transition: 'all 0.2s ease !important',
    fontWeight: '500 !important',
    '&:hover': {
      boxShadow: '0 2px 6px rgba(0,0,0,0.15) !important',
    },
  },
  // Time grid styling
  '& .fc-timegrid-axis': {
    width: '50px !important',
    minWidth: '50px !important',
    maxWidth: '50px !important',
    backgroundColor: '#f0f2ff !important',
    borderRight: '2px solid #4E67EB !important',
  },
  '& .fc-timegrid-slot-label-cushion': {
    fontSize: '0.8rem',
    padding: '0 4px !important',
    fontWeight: '500 !important',
    color: '#4E67EB !important',
  },
  // Day headers
  '& .fc-col-header-cell-cushion': {
    fontSize: '14px !important',
    fontWeight: '600 !important',
    padding: '10px 8px !important',
    textAlign: 'center !important',
    textTransform: 'none',
    whiteSpace: 'pre-line !important',
    lineHeight: '1.3 !important',
    backgroundColor: '#f0f2ff !important',
    color: '#4E67EB !important',
    borderBottom: '2px solid #4E67EB !important',
  },
  // Sunday styling - green color for weekend
  '& .fc-col-header-cell:nth-child(1) .fc-col-header-cell-cushion': {
    backgroundColor: '#d1fae5 !important',
    color: '#166534 !important',
    borderBottom: '2px solid #166534 !important',
  },
  // Custom scrollbar styling for the calendar
  '& .fc-scroller': {
    // Webkit browsers (Chrome, Safari, Edge)
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.1)',
      borderRadius: '4px',
      '&:hover': {
        background: 'rgba(0, 0, 0, 0.2)',
      },
    },
    '&::-webkit-scrollbar-corner': {
      background: 'transparent',
    },
    // Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0, 0, 0, 0.1) transparent',
  },
};

export const sharedCalendarCompactStyles = `
  /* Make toolbar more compact */
  .fc-toolbar {
    padding: 2px 4px !important;
    margin-bottom: 4px !important;
    min-height: 24px !important;
  }
  
  .fc-toolbar-chunk {
    gap: 8px !important;
  }
  
  /* Add more space around prev/next buttons */
  .fc-toolbar-chunk:first-child,
  .fc-toolbar-chunk:last-child {
    margin: 0 8px !important;
  }
  
  .fc-button {
    padding: 2px 8px !important;
    font-size: 0.65rem !important;
    min-height: 20px !important;
    line-height: 1 !important;
  }
  
  /* Make prev/next/today buttons much smaller */
  .fc-prev-button,
  .fc-next-button,
  .fc-today-button {
    padding: 1px 4px !important;
    font-size: 0.5rem !important;
    min-height: 12px !important;
    max-height: 12px !important;
    line-height: 1 !important;
    border-radius: 3px !important;
  }
  
  /* Add space between prev and next buttons */
  .fc-prev-button {
    margin-right: 4px !important;
  }
  
  .fc-next-button {
    margin-left: 4px !important;
  }
  
  .fc-prev-button .fc-icon,
  .fc-next-button .fc-icon {
    font-size: 0.4rem !important;
    line-height: 1 !important;
  }
  
  /* Make day headers more compact - balanced */
  .fc .fc-col-header-cell {
    padding: 6px 4px !important;
    height: 28px !important;
    min-height: 28px !important;
    max-height: 28px !important;
  }
  
  .fc .fc-col-header-cell-cushion {
    padding: 2px 6px !important;
    font-size: 0.75rem !important;
    line-height: 1.2 !important;
    border-bottom: none !important;
    text-decoration: none !important;
    background-color: transparent !important;
    background: none !important;
  }
  
  /* Extra specific targeting for header text underlines */
  .fc .fc-scrollgrid .fc-col-header-cell-cushion {
    border-bottom: none !important;
    text-decoration: none !important;
    background-color: transparent !important;
    background: none !important;
  }
  
  /* Make time slots more compact with 30-min granularity */
  .fc-timegrid-slot {
    height: 25px !important;
    min-height: 25px !important;
    max-height: 25px !important;
    border-top: 1px solid #e0e0e0 !important;
  }
  
  .fc-timegrid-slot-minor {
    height: 12px !important;
    min-height: 12px !important;
    max-height: 12px !important;
    border-top: 1px solid #f0f0f0 !important;
  }
  
  .fc-timegrid-slot-label {
    font-size: 0.65rem !important;
    padding: 2px 4px !important;
    line-height: 1 !important;
  }
  
  /* Make events more compact */
  .fc-event {
    font-size: 0.65rem !important;
    padding: 2px 4px !important;
    margin: 0 !important;
    line-height: 1 !important;
    min-height: 18px !important;
  }
  
  /* Compact time axis */
  .fc-timegrid-axis {
    width: 45px !important;
    font-size: 0.65rem !important;
    border-right: none !important;
  }
  
  /* More specific targeting for time axis border */
  .fc .fc-timegrid-axis,
  .fc .fc-timegrid-slot-label {
    border-right: none !important;
    border-left: none !important;
  }
  
  /* Keep borders for 30-min granularity */
  .fc-timegrid-body {
    border-spacing: 0 !important;
  }
  
  /* Hide toolbar title (date range between buttons) */
  .fc-toolbar-title {
    display: none !important;
  }
  
  /* Make time grid expand to fill available space */
  .fc-timegrid-body {
    height: auto !important;
  }
  
  .fc-timegrid-slots {
    height: auto !important;
  }
  
  .fc-timegrid-slot {
    height: auto !important;
    min-height: 2.5em !important;
  }
  
  /* Calendar grid hover cursors */
  .fc-timegrid-slot {
    cursor: crosshair !important;
    transition: background-color 0.2s ease !important;
  }
  
  .fc-timegrid-slot:hover {
    background-color: rgba(78, 103, 235, 0.08) !important;
  }
  
  /* Available time slots - indicate they can be selected */
  .fc-timegrid-slot:not(.fc-blocked-slot):not(.fc-day-off) {
    cursor: crosshair !important;
  }
  
  /* Blocked time slots - indicate they cannot be selected */
  .fc-timegrid-slot.fc-blocked-slot {
    cursor: not-allowed !important;
  }
  
  /* Day off slots */
  .fc-timegrid-slot.fc-day-off {
    cursor: not-allowed !important;
  }
  
  /* Existing bookings - indicate they cannot be selected */
  .fc-event.existing-booking {
    cursor: not-allowed !important;
  }
  
  /* Working hours indicator - non-interactive */
  .fc-event.working-hours-indicator {
    cursor: default !important;
  }
  
  /* Calendar day headers - non-interactive */
  .fc-col-header-cell {
    cursor: default !important;
  }
  
  /* Time axis - non-interactive */
  .fc-timegrid-axis {
    cursor: default !important;
  }
  
  /* Navigation buttons */
  .fc-button {
    cursor: pointer !important;
  }
  
  /* Month/Year title - non-interactive */
  .fc-monthYearTitle-button {
    cursor: default !important;
  }
`;

